import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import PublicIcon from "@mui/icons-material/Public";
import TranslateIcon from "@mui/icons-material/Translate";
import AppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import deutschFlag from "../../assets/icons/deutschFlag.svg";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";

import {
  FUNC_NOOP,
  drawerWidth,
  variantError,
  variantSuccess,
} from "../../constants/constants";
import { ROUTES } from "../../constants/routes";
import { Languages } from "../../types";
import { useTranslationsContext } from "../../contexts/TranslationsContext";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useUserContext } from "../../contexts/UserContext";
import { updateUser } from "../../api/users";
import { ParticifyLogo, TrakifyLogo, CalendFyLogo, LogifyLogo, ReportifyLogo } from "../../components/logos/ModuleLogos";
import NotificationBell from "../../modules/notify/components/NotificationBell";
import NotificationDrawer from "../../modules/notify/components/NotificationDrawer";
import { useNotifications } from "../../modules/notify/hooks/useNotifications";
import QuickObservationModal from "../../modules/logify/components/QuickObservationModal";

import "./AppBar.css";

type ActiveModule = "home" | "particify" | "trackify" | "calendfy" | "logify" | "reportify";

const MODULE_META: Record<Exclude<ActiveModule, "home">, { label: string; Logo: typeof ParticifyLogo; color: string }> = {
  particify:  { label: "ParticiFy",  Logo: ParticifyLogo,  color: "#7aacb3" },
  trackify:   { label: "TrackiFy",   Logo: TrakifyLogo,    color: "#CF8DA8" },
  calendfy:   { label: "CalendFy",   Logo: CalendFyLogo,   color: "#7aacb3" },
  logify:     { label: "LogiFy",     Logo: LogifyLogo,      color: "#9678c8" },
  reportify:  { label: "ReportiFy",  Logo: ReportifyLogo,   color: "#7aacb3" },
};

type HeaderProps = {
  onToggle?(): void;
  staticView?: boolean;
  activeModule?: ActiveModule;
  currentDrawerWidth?: number;
};

const TutorifySvgLogo = ({ size = 36 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 50 50"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
  >
    <rect width="50" height="50" rx="12" fill="#7aacb3" />
    <path
      d="M25 10L7 19l18 9 18-9-18-9z"
      fill="white"
      opacity="0.95"
    />
    <path
      d="M13 24v9c0 3.3 5.4 6 12 6s12-2.7 12-6v-9L25 30 13 24z"
      fill="white"
      opacity="0.7"
    />
    <rect
      x="37"
      y="19"
      width="2.5"
      height="10"
      rx="1.25"
      fill="white"
      opacity="0.9"
    />
    <circle cx="38.25" cy="31" r="2" fill="white" opacity="0.9" />
  </svg>
);

function Header({
  onToggle = FUNC_NOOP,
  staticView = false,
  activeModule = "home",
  currentDrawerWidth = drawerWidth,
}: HeaderProps) {
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const userContext = useUserContext();
  const {
    translation,
    setLanguage,
    language: currentLanguage,
  } = useTranslationsContext();
  const [languagesMenu, setLanguagesMenu] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickObsOpen, setQuickObsOpen] = useState(false);

  const isLoginOrSignUp =
    location.pathname === ROUTES.LOG_IN || location.pathname === ROUTES.SIGN_UP;
  const isForgotOrUpdatePassword =
    location.pathname.includes(ROUTES.UPDATE_PASSWORD.split(":")[0]) ||
    location.pathname === ROUTES.FORGOT_PASSWORD;
  const isAuthPage = isLoginOrSignUp || isForgotOrUpdatePassword;

  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    deleteOne,
  } = useNotifications({ skip: isAuthPage });
  const handleBellClick = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  // Snackbar when new notifications arrive
  const prevUnreadRef = useRef(unreadCount);
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current && prevUnreadRef.current >= 0) {
      const diff = unreadCount - prevUnreadRef.current;
      enqueueSnackbar(
        diff === 1
          ? (translation.NOTIFY_NEW_ONE?.defaultMessage || "New notification")
          : `${diff} ${translation.NOTIFY_NEW_MANY?.defaultMessage || "new notifications"}`,
        {
          variant: "info",
          autoHideDuration: 4000,
          style: {
            background: "linear-gradient(135deg, #342f50 0%, #2a2644 100%)",
            border: "1px solid rgba(122,172,179,0.3)",
            borderRadius: "10px",
            color: "#e0ddf0",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          },
          action: (key) => (
            <IconButton size="small" onClick={() => closeSnackbar(key)} sx={{ color: "#e0ddf0" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        },
      );
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount, enqueueSnackbar, closeSnackbar, translation]);

  const languagesMenuOpen = Boolean(languagesMenu);
  function handleLanguagesMenuClose() {
    setLanguagesMenu(null);
  }

  function handleLanguagesClick(event: React.MouseEvent<HTMLElement>) {
    setLanguagesMenu(event.currentTarget);
  }

  function handleAppIconClick() {
    if (
      location.pathname !== ROUTES.HOME &&
      location.pathname !== ROUTES.LOG_IN &&
      location.pathname !== ROUTES.SIGN_UP
    ) {
      window.scrollTo(0, 0);
      navigate(ROUTES.HOME, {
        state: { breadcrumbs: [] },
      });
    }
  }

  async function handleLanguageChange(language: Languages) {
    handleLanguagesMenuClose();
    if (currentLanguage !== language) {
      try {
        setLanguage(language);
        const langCode = language === Languages.ENGLISH ? "en" : "de";
        if (userContext.id) {
          await updateUser(userContext.id, { language: langCode });
        }
        enqueueSnackbar(translation.UPDATE_LANGUAGE_SUCCESS.defaultMessage, {
          variant: variantSuccess,
        });
      } catch (error) {
        console.error(error);
        enqueueSnackbar(translation.UPDATE_LANGUAGE_ERROR.defaultMessage, {
          variant: variantError,
        });
      }
    }
  }

  return (
    <AppBar
      position="fixed"
      className="appbar-root"
      data-module={activeModule}
      sx={{
        width: {
          md: staticView ? "100%" : `calc(100% - ${currentDrawerWidth}px)`,
          xs: "100%",
        },
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <Toolbar className="appbar-toolbar" sx={{ justifyContent: "space-between" }}>
        {/* Left: hamburger + brand */}
        <div className="flex items-center gap-2">
          {!staticView && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onToggle}
              sx={{ display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <div className="appbar-brand" onClick={handleAppIconClick}>
            <TutorifySvgLogo size={36} />
            <span className="appbar-wordmark">TutoriFy</span>
          </div>

          {activeModule !== "home" && (() => {
            const meta = MODULE_META[activeModule];
            return (
              <div className="appbar-module-badge" key={activeModule} style={{ "--module-color": meta.color } as React.CSSProperties}>
                <span className="appbar-module-sep">/</span>
                <div className="appbar-module-logo">
                  <meta.Logo size={20} />
                </div>
                <span className="appbar-module-name">{meta.label}</span>
              </div>
            );
          })()}
        </div>

        <div className="flex-1" />

        {/* Quick observation button — visible on all pages */}
        {!isLoginOrSignUp && !isForgotOrUpdatePassword && (
          <Button
            className="appbar-quick-obs-btn"
            onClick={() => setQuickObsOpen(true)}
            size="small"
            sx={{
              background: "var(--color-logify-primary)",
              borderRadius: "20px",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              px: 2,
              py: 0.5,
              mr: 1.5,
              "&:hover": { background: "#8568b8" },
            }}
          >
            {translation.LOGIFY_QUICK_BUTTON?.defaultMessage || "Beobachtung"}
          </Button>
        )}

        {/* Right: language + avatar */}
        <div className="appbar-actions">
          {(isLoginOrSignUp || isForgotOrUpdatePassword || isMobile) && (
            <div className="invisible">
              <IconButton size="small">
                <TranslateIcon color="secondary" />
              </IconButton>
            </div>
          )}
          <Tooltip title={translation.LANGUAGES.defaultMessage}>
            <IconButton
              className="appbar-lang-btn"
              onClick={handleLanguagesClick}
              size="small"
              aria-controls={languagesMenuOpen ? "languages-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={languagesMenuOpen ? "true" : undefined}
            >
              <TranslateIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          {!isLoginOrSignUp && !isForgotOrUpdatePassword && (
            <NotificationBell
              unreadCount={unreadCount}
              onClick={handleBellClick}
            />
          )}
        </div>
      </Toolbar>

      {/* Language menu */}
      <Menu
        anchorEl={languagesMenu}
        id="languages-menu"
        open={languagesMenuOpen}
        onClose={handleLanguagesMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => handleLanguageChange(Languages.DEUTSCH)}>
          <ListItemIcon>
            <img className="w-6 h-6" src={deutschFlag} alt="DE" />
          </ListItemIcon>
          Deutsch
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleLanguageChange(Languages.ENGLISH)}>
          <ListItemIcon>
            <PublicIcon />
          </ListItemIcon>
          English
        </MenuItem>
      </Menu>

      {!isLoginOrSignUp && !isForgotOrUpdatePassword && (
        <NotificationDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          notifications={notifications}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onDelete={deleteOne}
        />
      )}

      <QuickObservationModal open={quickObsOpen} onClose={() => setQuickObsOpen(false)} />
    </AppBar>
  );
}

export default Header;
